import {ImpersonationBannerComponent} from './components/impersonation-banner/impersonation-banner.component';
import {Component, HostListener, inject, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent, IconDefinition} from '@fortawesome/angular-fontawesome';
import {
  faArrowLeftFromBracket, faArrowLeftLongToLine,
  faBars,
  faBell,
  faCalculator,
  faChevronDown,
  faGear,
  faHouse,
  faListCheck,
  faMagnifyingGlass,
  faShapes,
  faUser,
  faUsers,
  faXmark,
  faChartPieSimple
} from '@fortawesome/sharp-regular-svg-icons';
import {NavigationEnd, Route, Router, RouterLink} from '@angular/router';
import {
  IonApp,
  IonButton,
  IonButtons,
  IonContent,
  IonMenu, IonMenuButton,
  IonMenuToggle,
  IonRouterLinkWithHref,
  IonRouterOutlet,
  IonSplitPane,
  IonToolbar,
  MenuController, ModalController,
} from '@ionic/angular';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {filter, finalize} from 'rxjs';
import {customerRoutes} from './pages/customer/customer.routes';
import {optionsRoutes} from './pages/options/options.routes';
import {AuthService} from './services/auth/auth';
import {faDial, faMobileRetro, faLocationDot, faEllipsis, faChartLineUp} from "@fortawesome/sharp-light-svg-icons";
import {UserMenuComponent} from "./modals/user-menu/user-menu.component";
import {UserModel} from "./models/user.model";
import {UserAvatarComponent} from './components/user-avatar/user-avatar.component';
import {DashboardEditModeService} from './services/dashboard/dashboard-edit-mode';

type AdminNavItem = {
  activePrefix?: string;
  activePrefixes?: string[];
  children?: AdminNavChildItem[];
  disabled?: boolean;
  exact?: boolean;
  icon: IconDefinition;
  label: string;
  order: number;
  route: string;
};

type AdminNavChildItem = {
  activePrefixes?: string[];
  disabled?: boolean;
  exact?: boolean;
  label: string;
  order: number;
  route: string;
};

type AdminNavSection = {
  id: string;
  items: AdminNavItem[];
  label: string;
  modifier?: 'center' | 'bottom';
  order: number;
};

type RouteNavData = {
  activePrefix?: string;
  activePrefixes?: string[];
  exact?: boolean;
  icon?: string;
  label: string;
  order?: number;
  route?: string;
  section?: string;
  activeRoutes?: string[],
  disabled?: boolean;
};

@Component({
  standalone: true,
  selector: 'app-root',
  host: {'[style.--mobile-floating-button-offset.px]': 'mobileFloatingButtonOffset'},
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IonApp,
    ImpersonationBannerComponent,
    IonButton,
    IonButtons,
    IonContent,
    IonMenu,
    IonMenuToggle,
    IonRouterLinkWithHref,
    IonRouterOutlet,
    IonSplitPane,
    IonToolbar,
    RouterLink,
    FaIconComponent,
    TranslatePipe,
    UserAvatarComponent,
    IonMenuButton,
  ],
})
export class AppComponent {
  private readonly authService: AuthService = inject(AuthService);
  private readonly dashboardEditModeService: DashboardEditModeService = inject(DashboardEditModeService);
  private readonly menuController: MenuController = inject(MenuController);
  private readonly router: Router = inject(Router);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly modalController: ModalController = inject(ModalController);


  private readonly navIcons: Record<string, IconDefinition> = {
    'audit-log': faListCheck,
    customers: faUsers,
    home: faHouse,
    locations: faLocationDot,
    modules: faCalculator,
    options: faGear,
    user: faUser,
    'chart-line-up': faChartLineUp,
    'chart-pie-simple': faChartPieSimple,
    'home-outline': faHouse,
    'people-outline': faUsers,
    'notifications': faBell
  };

  protected readonly homeRoutes: string[] = ['home', 'location'];
  protected readonly statisticsRoutes: string[] = ['/statistics'];
  protected readonly moduleRoutes: string[] = ['module'];
  protected readonly profileRoutes: string[] = ['user', 'notifications'];
  protected readonly reportsAndTrendsRoutes: string[] = ['reports-and-trends'];
  protected readonly optionsRoutes: string[] = ['options'];
  protected readonly reportsRoutes: string[] = ['report-templates'];

  protected readonly adminNavSections: AdminNavSection[] = this.buildAdminNavSections();
  protected currentUrl: string = this.router.url;
  protected isCompactViewportActive: boolean = this.isCompactViewport();
  protected isSidebarCollapsed: boolean = false;
  protected isLoggingOut: boolean = false;
  protected readonly openSubmenus = new Set<string>();

  private readonly shellHiddenRoutePrefixes: string[] = [
    '/login',
    '/register',
    '/auth/two-fa-confirm',
    '/auth/two-fa-setup',
    '/two-fa-confirm',
    '/two-fa-setup',
  ];

  private readonly topBarHidden: string[] = [
    '/error'
  ]

  protected readonly customerRoutes: RouteNavData[] = [
    {
      label: 'nav.dashboard',
      route: 'home',
      activeRoutes: this.homeRoutes
    }, {
      label: 'nav.trends',
      route: 'reports-and-trends',
      activeRoutes: this.reportsAndTrendsRoutes
    }, {
      label: 'nav.controller',
      route: '/module/customer/modules',
      disabled: true
    }, {
      label: 'nav.modules',
      route: '/module/modules',
      activeRoutes: this.moduleRoutes
    },
    {
      label: 'nav.reports',
      route: 'report-templates/dashboard',
      activeRoutes: this.reportsRoutes
    },
    {
      label: 'nav.user_management',
      route: 'user/detail',
      activeRoutes: this.profileRoutes
    }
  ];

  private userMenModal: HTMLIonModalElement | undefined;
  protected userMenuModalOpen: boolean = false;

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;

        if (!this.isDashboardEditingRoute(this.currentUrl)) {
          this.dashboardEditModeService.leave();
        }
      });
  }

  @HostListener('window:resize')
  protected handleWindowResize(): void {
    this.isCompactViewportActive = this.isCompactViewport();
  }

  protected get isImpersonating(): boolean { return this.authService.isImpersonating; }

  protected get mobileFloatingButtonOffset(): number {
    return this.authService.isRootAdmin() ? 0 : 64;
  }

  protected get isShellVisible(): boolean {
    return this.authService.isRootAdmin()
      && !this.shellHiddenRoutePrefixes.some((route: string) => this.currentUrl.startsWith(route));
  }

  protected get isAuthenticated(): boolean {
    return this.authService.authReady() && this.authService.isAuthenticated() && !this.authService.isRootAdmin() && !this.shellHiddenRoutePrefixes.some((route: string) => this.currentUrl.startsWith(route));
  }

  protected get hasAccountNavigation(): boolean {
    return this.adminNavSections.some((section: AdminNavSection) => section.id === 'account');
  }

  protected get sidebarUser(): UserModel | null {
    return this.authService.currentUser();
  }

  protected get sidebarUserName(): string {
    const user = this.authService.currentUser();
    return [user?.first_name, user?.last_name].filter(Boolean).join(' ') || this.translateService.instant('user.admin_user');
  }

  protected get sidebarUserEmail(): string {
    return this.authService.currentUser()?.email ?? 'admin@energymaster.local';
  }

  protected get showHeader(): boolean {
    return !this.topBarHidden.some((route: string) => this.currentUrl.startsWith(route))
  }

  protected toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  protected async closeMenu(): Promise<void> {
    await this.menuController.close('admin-menu');
  }

  protected async openMenu(): Promise<void> {
    await this.menuController.open('admin-menu');
  }

  protected logout(): void {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;

    this.authService.apiLogout()
      .pipe(finalize(() => {
        this.isLoggingOut = false;
        void this.menuController.close('admin-menu');
        void this.router.navigateByUrl('/login', {replaceUrl: true});
      }))
      .subscribe({
        error: () => undefined,
      });
  }

  protected hasChildren(item: AdminNavItem): boolean {
    return Boolean(item.children?.length);
  }

  protected isSubmenuOpen(item: AdminNavItem): boolean {
    return this.openSubmenus.has(item.route);
  }

  protected toggleSubmenu(item: AdminNavItem): void {
    if (!this.hasChildren(item)) {
      return;
    }

    if(this.isSidebarCollapsed){
      this.router.navigate([item.route]);
      return;
    }

    if (this.openSubmenus.has(item.route)) {
      this.openSubmenus.delete(item.route);
      return;
    }

    this.openSubmenus.clear();
    this.openSubmenus.add(item.route);
  }

  protected isItemActive(item: AdminNavItem): boolean {
    const activePrefixes = [item.activePrefix ?? item.route, ...(item.activePrefixes ?? [])];
    return activePrefixes.some(prefix => this.matchesRoutePrefix(this.currentUrl, prefix))
      || Boolean(item.children?.some((child: AdminNavChildItem) => this.isChildItemActive(child)));
  }

  protected isChildItemActive(item: AdminNavChildItem): boolean {
    const currentPath = this.currentUrl.split(/[?#]/, 1)[0];
    const routeMatches = item.exact ? currentPath === item.route : currentPath.startsWith(item.route);
    return routeMatches || Boolean(item.activePrefixes?.some(prefix => this.matchesRoutePrefix(currentPath, prefix)));
  }

  private isDashboardEditingRoute(url: string): boolean {
    return url === '/home' || url.startsWith('/dashboard/configure/');
  }

  private buildAdminNavSections(): AdminNavSection[] {
    const sections = new Map<string, AdminNavSection>([
      ['main', {id: 'main', items: [], label: 'nav.main_navigation', order: 10}],
      ['admin', {id: 'admin', items: [], label: 'nav.admin_navigation', modifier: 'center', order: 20}],
      ['account', {id: 'account', items: [], label: 'nav.account_navigation', modifier: 'bottom', order: 30}],
    ]);

    for (const route of this.router.config) {
      const navData = route.data?.['nav'] as RouteNavData | undefined;

      if (!navData || !route.path || route.path.includes(':')) {
        continue;
      }

      const section = sections.get(navData.section ?? 'main');

      if (!section) {
        continue;
      }

      const routeBasePath = `/${route.path}`;
      const routePath = navData.route ?? routeBasePath;
      const children = this.buildGroupChildren(route.path, routeBasePath);

      section.items.push({
        activePrefix: navData.activePrefix,
        activePrefixes: navData.activePrefixes,
        children,
        exact: navData.exact,
        icon: this.resolveNavIcon(navData.icon),
        label: navData.label,
        order: navData.order ?? 0,
        route: routePath,
      });

      if (route.path === 'module') {
        section.items.push(this.buildTypesAndTemplatesNavItem());
      }
    }

    return Array.from(sections.values())
      .map((section: AdminNavSection) => ({
        ...section,
        items: section.items.sort((first: AdminNavItem, second: AdminNavItem) => first.order - second.order),
      }))
      .filter((section: AdminNavSection) => section.items.length > 0)
      .sort((first: AdminNavSection, second: AdminNavSection) => first.order - second.order);
  }

  private buildGroupChildren(path: string, basePath: string): AdminNavChildItem[] | undefined {
    if (path === 'customer') {
      return this.buildChildNavItems(basePath, customerRoutes);
    }

    if (path === 'module') {
      return [
        {label: 'nav.controller_management', order: 10, route: '/module/controller'},
        {label: 'nav.module_management', order: 20, route: '/module/modules'},
        {label: 'nav.meter_management', order: 30, route: '/module/meters'},
        {label: 'nav.service_book_management', order: 40, route: '/module/service-books'},
        {activePrefixes: ['/csv-imports'], exact: true, label: 'csv_import.title', order: 50, route: '/csv-imports'},
      ];
    }

    if (path === 'user') {
      // User administration routes.
      return [
        {activePrefixes: ['/user/detail'], label: 'nav.user_overview', order: 10, route: '/user/dashboard'},
        {label: 'nav.user_edit', order: 20, route: `/user/edit`},
        {label: 'impersonation.title', order: 30, route: '/user/impersonate'}
      ];
    }

    if (path === 'options') {
      return this.buildChildNavItems(basePath, optionsRoutes);
    }

    return undefined;
  }

  private buildTypesAndTemplatesNavItem(): AdminNavItem {
    return {
      children: [
        {
          label: 'nav.controller_type_management',
          order: 10,
          route: '/module/controll-types',
        },
        {label: 'nav.module_type_management', order: 20, route: '/module/module-types'},
        {label: 'nav.module_template_management', order: 30, route: '/module/module-templates'},
        {label: 'nav.meter_types', order: 35, route: '/module/meter-types'},
        {
          label: 'nav.dashboard_template_management',
          order: 40,
          route: '/dashboard-templates',
        },
      ],
      icon: faShapes,
      label: 'nav.types_and_templates',
      order: 40,
      route: '/module/module-types',
    };
  }

  private buildChildNavItems(parentRoute: string, routes: Route[]): AdminNavChildItem[] {
    return routes
      .map((route: Route): AdminNavChildItem | null => {
        const navData = route.data?.['nav'] as RouteNavData | undefined;

        if (!navData || !route.path || route.path.includes(':')) {
          return null;
        }

        return {
          exact: navData.exact,
          label: navData.label,
          order: navData.order ?? 0,
          route: `${parentRoute}/${route.path}`,
        };
      })
      .filter((item: AdminNavChildItem | null): item is AdminNavChildItem => item !== null)
      .sort((first: AdminNavChildItem, second: AdminNavChildItem) => first.order - second.order);
  }

  private resolveNavIcon(icon?: string): IconDefinition {
    return this.navIcons[icon ?? 'home'] ?? faHouse;
  }

  private matchesRoutePrefix(url: string, prefix: string): boolean {
    const path = url.split(/[?#]/, 1)[0];
    return path === prefix || path.startsWith(`${prefix}/`);
  }

  private isCompactViewport(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 1199px)').matches;
  }

  protected async openUserMenuModal(): Promise<void> {
    const user: UserModel | null = this.authService.currentUser();
    if (!this.userMenModal || !this.userMenuModalOpen) {
      this.userMenModal = await this.modalController.create({
        component: UserMenuComponent,
        componentProps: {
          user: user
        },
        id: 'userMenu',
        cssClass: 'userMenuModal'
      })

      this.userMenuModalOpen = true;
      await this.userMenModal.present();

      await this.userMenModal.onDidDismiss().then(() => {
        this.userMenuModalOpen = false;
      });
    } else if (this.userMenModal) {
      await this.userMenModal.dismiss(null, 'cancel');
    }
  }

  protected async routeTo(route: string): Promise<void> {
    await this.router.navigate([route]);
    await this.userMenModal?.dismiss(null, 'cancel');
  }

  protected isActiveRoute(routes: string[]): boolean {
    const currentUrl: string = this.router.url.split('?')[0].replace(/\/$/, '');

    if(this.userMenuModalOpen){
      return false;
    }
    return routes.some((route: string) => {
      const normalizedRoute: string = `/${route}`.replace(/\/$/, '');
      return currentUrl === normalizedRoute || currentUrl.startsWith(`${normalizedRoute}/`);
    });
  }

  protected readonly faArrowLeftLongToLine: IconDefinition = faArrowLeftLongToLine;
  protected readonly faMagnifyingGlass: IconDefinition = faMagnifyingGlass;
  protected readonly faArrowLeftFromBracket: IconDefinition = faArrowLeftFromBracket;
  protected readonly faBars: IconDefinition = faBars;
  protected readonly faBell: IconDefinition = faBell;
  protected readonly faChevronDown: IconDefinition = faChevronDown;
  protected readonly faXmark: IconDefinition = faXmark;
  protected readonly faEllipsis: IconDefinition = faEllipsis;
  protected readonly faMobileRetro: IconDefinition = faMobileRetro;
  protected readonly faDial: IconDefinition = faDial;
  protected readonly faLocationDot: IconDefinition = faLocationDot;
  protected readonly faChartLineUp: IconDefinition = faChartLineUp;
}
